import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { toastSuccess, toastError } from '../utils/toasts';
import { Button, Icon } from '../components/ui';

export default function Backup() {
  const [backups, setBackups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backup');
      setBackups(res.data.backups || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Erreur chargement sauvegardes:', err);
      toastError('Impossible de charger les sauvegardes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await api.post('/backup');
      toastSuccess('Sauvegarde créée avec succès');
      await fetchBackups();
    } catch (err) {
      toastError('Erreur lors de la création de la sauvegarde');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (backup) => {
    const token = localStorage.getItem('token');
    const workspaceId = localStorage.getItem('active_workspace_id');
    const baseURL = api.defaults.baseURL;
    const headers = new Headers({ Authorization: `Bearer ${token}` });
    if (workspaceId) headers.set('x-workspace-id', workspaceId);

    fetch(`${baseURL}/backup/${backup._id}/download`, { headers })
      .then(res => {
        if (!res.ok) throw new Error('Erreur téléchargement');
        return res.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = backup.fileName;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toastError('Erreur lors du téléchargement'));
  };

  const handleEmailSend = async (backupId) => {
    if (!emailAddress) { toastError('Veuillez entrer une adresse email'); return; }
    try {
      await api.post(`/backup/${backupId}/email`, { email: emailAddress });
      toastSuccess('Email envoyé avec succès');
      setEmailModal(null);
      setEmailAddress('');
      await fetchBackups();
    } catch (err) {
      toastError("Erreur lors de l'envoi de l'email");
    }
  };

  const handleDelete = async (backupId) => {
    try {
      await api.delete(`/backup/${backupId}`);
      toastSuccess('Sauvegarde supprimée');
      await fetchBackups();
    } catch (err) {
      toastError('Erreur lors de la suppression');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Sauvegardes</h1>
          <p className="text-gray-600">Gérez les sauvegardes de vos données</p>
        </div>
        <Button variant="primary" onClick={handleCreateBackup} disabled={creating}>
          <Icon name="IconDatabase" size={16} className="mr-2" />
          {creating ? 'Création...' : 'Créer une sauvegarde'}
        </Button>
      </div>

      {creating && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Icon name="IconLoader" size={20} className="animate-spin text-blue-600" />
          <span className="text-blue-700">Création de la sauvegarde en cours...</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            Historique des sauvegardes ({total})
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchBackups} disabled={loading}>
            <Icon name="IconSync" size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {loading && !backups.length ? (
          <div className="flex justify-center items-center py-12">
            <Icon name="IconLoader" size={24} className="animate-spin text-blue-600" />
          </div>
        ) : backups.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Icon name="IconDatabase" size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium mb-1">Aucune sauvegarde</p>
            <p className="text-sm">Cliquez sur "Créer une sauvegarde" pour commencer</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Taille</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {backups.map((b) => (
                <tr key={b._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{formatDate(b.createdAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      b.trigger === 'automatic' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {b.trigger === 'automatic' ? 'Automatique' : 'Manuelle'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      b.status === 'completed' ? 'bg-green-100 text-green-700' :
                      b.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {b.status === 'in_progress' && <Icon name="IconLoader" size={12} className="animate-spin" />}
                      {b.status === 'completed' ? 'Terminée' : b.status === 'failed' ? 'Échouée' : 'En cours'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatSize(b.fileSize)}</td>
                  <td className="px-4 py-3 text-right">
                    {b.status === 'completed' && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleDownload(b)} title="Télécharger"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Icon name="IconDownload" size={16} />
                        </button>
                        <button onClick={() => setEmailModal(b._id)} title="Envoyer par email"
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                          <Icon name="IconMail" size={16} />
                        </button>
                        <button onClick={() => handleDelete(b._id)} title="Supprimer"
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Icon name="IconTrash" size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {emailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Envoyer la sauvegarde par email</h3>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="exemple@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setEmailModal(null); setEmailAddress(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                Annuler
              </button>
              <button onClick={() => handleEmailSend(emailModal)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
